import { useEffect, useRef } from 'react';
import useReducedMotion from '../../hooks/useReducedMotion';
import useIsDark from '../../hooks/useIsDark';

const BUBBLE_COUNT = 25;
const POP_PARTICLE_COUNT = 28;
const POP_SOUND_COUNT = 4;
const TRANSITION_DURATION = 1; // seconds, fade-in when entering light mode

function createPopSounds() {
  let audioCtx = null;
  const buffers = [];
  let loaded = false;
  let loading = false;

  function ensureCtx() {
    if (!audioCtx) {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioCtx;
  }

  function loadBuffers() {
    if (loaded || loading) return;
    loading = true;
    const ctx = ensureCtx();
    Promise.all(
      Array.from({ length: POP_SOUND_COUNT }, (_, i) =>
        fetch(`${process.env.PUBLIC_URL}/sounds/pop${i + 1}.mp3`)
          .then((res) => res.arrayBuffer())
          .then((buf) => ctx.decodeAudioData(buf))
      )
    ).then((decoded) => {
      buffers.push(...decoded);
      loaded = true;
    }).catch(() => { loading = false; });
  }

  let lastIndex = -1;

  return {
    preload() { loadBuffers(); },
    play(size, maxSize = 50, xPosition = 0, canvasWidth = 1) {
      if (!loaded || buffers.length === 0) { loadBuffers(); return; }
      const ctx = ensureCtx();
      if (ctx.state === 'suspended') ctx.resume();

      let index;
      do {
        index = Math.floor(Math.random() * buffers.length);
      } while (index === lastIndex && buffers.length > 1);
      lastIndex = index;

      const source = ctx.createBufferSource();
      source.buffer = buffers[index];

      const gain = ctx.createGain();
      const sizeRatio = size / maxSize;
      gain.gain.value = 0.15 + sizeRatio * 0.1;

      const playbackRate = 1.3 - sizeRatio * 0.5;
      source.playbackRate.value = playbackRate + (Math.random() - 0.5) * 0.1;

      const panner = ctx.createStereoPanner();
      panner.pan.value = Math.max(-1, Math.min(1, (xPosition / canvasWidth) * 2 - 1));

      source.connect(gain).connect(panner).connect(ctx.destination);
      source.start();
    },
    dispose() {
      if (audioCtx) audioCtx.close();
    },
  };
}

function createBubble(w, h) {
  return {
    x: Math.random() * w,
    y: h + Math.random() * h * 0.5,
    size: 15 + Math.random() * 35,
    speed: 0.15 + Math.random() * 0.3,
    wobbleSpeed: 0.3 + Math.random() * 0.5,
    wobbleAmp: 15 + Math.random() * 25,
    hueOffset: Math.random() * 360,
    phase: Math.random() * Math.PI * 2,
    opacity: 0.18 + Math.random() * 0.12,
    popped: false,
  };
}

export default function Bubbles() {
  const canvasRef = useRef(null);
  const reducedMotion = useReducedMotion();
  const { isDarkRef } = useIsDark();

  useEffect(() => {
    if (reducedMotion) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    let canvasOpacity = isDarkRef.current ? 0 : 1;
    let targetOpacity = canvasOpacity;
    canvas.style.opacity = canvasOpacity;

    let bubbles = [];
    let popParticles = [];
    const popSounds = createPopSounds();
    if (!isDarkRef.current) popSounds.preload();

    const initBubbles = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      bubbles = Array.from({ length: BUBBLE_COUNT }, () =>
        createBubble(canvas.width, canvas.height)
      );
      bubbles.forEach((b) => {
        b.y = Math.random() * canvas.height * 1.5;
      });
    };

    const onThemeChange = (e) => {
      if (e.detail.isDark) {
        // Immediately hide bubbles when entering dark mode
        canvasOpacity = 0;
        targetOpacity = 0;
        canvas.style.opacity = 0;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        bubbles = [];
        popParticles = [];
      } else {
        if (bubbles.length === 0) {
          initBubbles();
        }
        targetOpacity = 1;
      }
    };
    document.documentElement.addEventListener('themechange', onThemeChange);

    let time = 0;
    let lastFrameTime = 0;
    let mouseX = -1000;
    let mouseY = -1000;

    const handleMouseMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
    };

    const handleClick = (e) => {
      if (isDarkRef.current) return;
      const cx = e.clientX;
      const cy = e.clientY;

      for (let i = bubbles.length - 1; i >= 0; i--) {
        const b = bubbles[i];
        if (b.popped) continue;
        const wobbleX = Math.sin(time * b.wobbleSpeed + b.phase) * b.wobbleAmp;
        const drawX = b.x + wobbleX;
        const dx = cx - drawX;
        const dy = cy - b.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < b.size + 10) {
          const hue = (time * 20 + b.hueOffset) % 360;
          for (let j = 0; j < POP_PARTICLE_COUNT; j++) {
            const angle = (Math.PI * 2 * j) / POP_PARTICLE_COUNT + (Math.random() - 0.5) * 0.5;
            const speed = 1.5 + Math.random() * 3;
            popParticles.push({
              x: drawX + (Math.random() - 0.5) * b.size * 0.5,
              y: b.y + (Math.random() - 0.5) * b.size * 0.5,
              vx: Math.cos(angle) * speed,
              vy: Math.sin(angle) * speed,
              size: 1.2 + Math.random() * 2,
              life: 0,
              maxLife: 35 + Math.random() * 20,
              hue: (hue + Math.random() * 120) % 360,
              opacity: 0.5 + Math.random() * 0.3,
            });
          }
          popSounds.play(b.size, 50, drawX, canvas.width);
          Object.assign(b, createBubble(canvas.width, canvas.height));
          b.y = canvas.height + b.size + Math.random() * 100;
          break;
        }
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('click', handleClick);

    let hoveringBubble = false;

    const drawBubbles = (dt) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      time += dt;

      let isHovering = false;

      for (let i = 0; i < bubbles.length; i++) {
        const b = bubbles[i];
        b.y -= b.speed;
        const wobbleX = Math.sin(time * b.wobbleSpeed + b.phase) * b.wobbleAmp;
        const drawX = b.x + wobbleX;

        const dx = mouseX - drawX;
        const dy = mouseY - b.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < b.size + 10) {
          isHovering = true;
        }
        if (dist < 60 && dist > 0) {
          const push = (1 - dist / 60) * 0.2;
          b.x -= (dx / dist) * push;
          b.y -= (dy / dist) * push;
        }

        if (b.y + b.size < -20) {
          Object.assign(b, createBubble(canvas.width, canvas.height));
          b.y = canvas.height + b.size + Math.random() * 60;
        }

        const hue1 = (time * 20 + b.hueOffset) % 360;
        const hue2 = (hue1 + 60) % 360;
        const hue3 = (hue1 + 180) % 360;

        const ring = ctx.createConicGradient(time * 0.5 + b.phase, drawX, b.y);
        ring.addColorStop(0, `hsla(${hue1}, 80%, 75%, ${b.opacity})`);
        ring.addColorStop(0.33, `hsla(${hue2}, 70%, 70%, ${b.opacity})`);
        ring.addColorStop(0.66, `hsla(${hue3}, 75%, 72%, ${b.opacity})`);
        ring.addColorStop(1, `hsla(${hue1}, 80%, 75%, ${b.opacity})`);

        ctx.beginPath();
        ctx.arc(drawX, b.y, b.size, 0, Math.PI * 2);
        ctx.strokeStyle = ring;
        ctx.lineWidth = 2;
        ctx.stroke();

        const fill = ctx.createRadialGradient(
          drawX - b.size * 0.3, b.y - b.size * 0.3, b.size * 0.1,
          drawX, b.y, b.size
        );
        fill.addColorStop(0, `hsla(${hue1}, 60%, 90%, ${b.opacity * 0.6})`);
        fill.addColorStop(0.5, `hsla(${hue2}, 50%, 85%, ${b.opacity * 0.3})`);
        fill.addColorStop(1, `hsla(${hue3}, 40%, 80%, ${b.opacity * 0.1})`);

        ctx.beginPath();
        ctx.arc(drawX, b.y, b.size, 0, Math.PI * 2);
        ctx.fillStyle = fill;
        ctx.fill();

        const hlSize = b.size * 0.25;
        const hlX = drawX - b.size * 0.25;
        const hlY = b.y - b.size * 0.3;
        const hl = ctx.createRadialGradient(hlX, hlY, 0, hlX, hlY, hlSize);
        hl.addColorStop(0, `rgba(255, 255, 255, ${b.opacity * 1.5})`);
        hl.addColorStop(1, 'rgba(255, 255, 255, 0)');

        ctx.beginPath();
        ctx.arc(hlX, hlY, hlSize, 0, Math.PI * 2);
        ctx.fillStyle = hl;
        ctx.fill();
      }

      if (isHovering !== hoveringBubble) {
        hoveringBubble = isHovering;
        document.body.dataset.bubbleHover = isHovering ? '1' : '';
      }

      for (let i = popParticles.length - 1; i >= 0; i--) {
        const p = popParticles[i];
        p.life++;
        p.x += p.vx;
        p.y += p.vy;
        p.vx *= 0.94;
        p.vy *= 0.94;

        if (p.life >= p.maxLife) {
          popParticles.splice(i, 1);
          continue;
        }

        const progress = p.life / p.maxLife;
        const alpha = (1 - progress) * p.opacity;
        const size = p.size * (1 - progress * 0.6);

        ctx.beginPath();
        ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${p.hue}, 70%, 75%, ${alpha})`;
        ctx.fill();
      }
    };

    const resize = () => {
      if (!isDarkRef.current) {
        const oldW = canvas.width || window.innerWidth;
        const oldH = canvas.height || window.innerHeight;
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
        const sx = canvas.width / oldW;
        const sy = canvas.height / oldH;
        bubbles.forEach((b) => {
          b.x *= sx;
          b.y *= sy;
        });
      }
    };

    if (!isDarkRef.current) {
      initBubbles();
    }

    let raf;

    const draw = (timestamp) => {
      if (document.hidden) {
        raf = requestAnimationFrame(draw);
        return;
      }
      const dt = lastFrameTime ? Math.min((timestamp - lastFrameTime) / 1000, 0.05) : 0.016;
      lastFrameTime = timestamp;

      if (canvasOpacity !== targetOpacity) {
        const opacityDelta = dt / TRANSITION_DURATION;
        if (targetOpacity > canvasOpacity) {
          canvasOpacity = Math.min(canvasOpacity + opacityDelta, 1);
        } else {
          canvasOpacity = Math.max(canvasOpacity - opacityDelta, 0);
        }
        canvas.style.opacity = canvasOpacity;

        if (canvasOpacity === 0) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          bubbles = [];
          popParticles = [];
        }
      }

      if (canvasOpacity > 0) {
        if (bubbles.length === 0) {
          initBubbles();
        }
        drawBubbles(dt);
      }
      raf = requestAnimationFrame(draw);
    };

    window.addEventListener('resize', resize);
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('click', handleClick);
      document.documentElement.removeEventListener('themechange', onThemeChange);
      popSounds.dispose();
      delete document.body.dataset.bubbleHover;
    };
  }, [reducedMotion, isDarkRef]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
        opacity: reducedMotion ? 0 : undefined,
      }}
    />
  );
}
